// Student API service — real API calls replacing mock data

import { apiRequest, API_BASE_URL } from './api';

// ── Raw API response types ──────────────────────────────────

export interface ParentRecord {
    id: string;
    first_name: string;
    last_name: string | null;
    name?: string;
    email: string | null;
    phone: string | null;
    // Only present in detail response
    relationship_type?: string | null;
    is_primary?: boolean;
}

/** Shape returned by the API for a single student record.
 *  List response (GET /api/students) has flat name fields at top level.
 *  Detail response (GET /api/students/{id}) nests everything under `user`. */
export interface StudentApiRecord {
    id: string;
    organization_id?: string;
    enrollment_number: string | null;
    roll_number?: string | null;
    admission_date?: string | null;
    enrollment_status: 'active' | 'inactive' | 'graduated' | 'transferred';
    stream_id?: string | null;
    aadhaar_number?: string | null;
    // The route the student rides, when they're on one. Only the detail response
    // (GET /api/students/{id}) carries it — the list response does not.
    route_id?: string | null;
    // Older/other payloads for the same thing; read through studentTransportRouteId().
    transport_route_id?: string | null;
    transport_route?: { id: string; title?: string } | null;
    metadata?: Record<string, unknown> | null;
    created_at?: string;
    updated_at?: string;
    // Flat name fields — present in list response
    first_name?: string;
    last_name?: string | null;
    name?: string;
    // Flat guardian/address fields — present in list response. The detail response
    // carries the same data as `user.address` + the `parents` array instead.
    address?: string | null;
    father_name?: string | null;
    mother_name?: string | null;
    father_contact?: string | null;
    // Flat in the list response; the detail response carries it as `user.date_of_birth`.
    date_of_birth?: string | null;
    // Parents array — present in both list and detail responses
    parents?: ParentRecord[];
    // Rich fields — only in detail response
    school?: { id: string; name: string; school_code: string } | null;
    user?: {
        id: string;
        first_name: string;
        last_name: string | null;
        email: string | null;
        phone: string | null;
        date_of_birth: string | null;
        gender: string | null;
        address: string | null;
        city: string | null;
        state: string | null;
        postal_code: string | null;
        country: string | null;
        is_active?: boolean;
        profile_photo_url?: string | null;
        profile_photo_thumb_url?: string | null;
    };
    current_class: { id: string; name: string } | null;
    current_section: { id: string; name: string } | null;
}

export interface StudentListResponse {
    success: boolean;
    message: string;
    data: StudentApiRecord[];
    meta: {
        current_page: number;
        from?: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

export interface ClassOption {
    id: string;
    name: string;
}

export interface SectionOption {
    id: string;
    name: string;
    class_id: string;
}

export interface StreamOption {
    id: string;
    name: string;
    code: string;
}

// ── API Functions ───────────────────────────────────────────

/**
 * List students with pagination, search, and filters.
 */
export async function fetchStudents(params: {
    page?: number;
    perPage?: number;
    name?: string;
    classId?: string;
    sectionId?: string;
}): Promise<StudentListResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.perPage) query.set('per_page', String(params.perPage));
    if (params.name) query.set('name', params.name);
    if (params.classId) query.set('class_id', params.classId);
    if (params.sectionId) query.set('section_id', params.sectionId);

    const qs = query.toString();
    const response = await fetch(
        `${API_BASE_URL}/api/students${qs ? `?${qs}` : ''}`,
        {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
            },
        }
    );

    const json = await response.json();

    if (!response.ok || !json.success) {
        throw { success: false, message: json.message || 'Failed to fetch students' };
    }

    return json as StudentListResponse;
}

/**
 * Get a single student by ID.
 */
export async function fetchStudent(id: string): Promise<StudentApiRecord> {
    const res = await apiRequest<StudentApiRecord>(`/api/students/${id}`);
    return res.data;
}

/**
 * Register (create) a new student.
 * Accepts FormData for multipart/form-data (required by API for file uploads).
 */
export async function createStudent(data: FormData): Promise<StudentApiRecord> {
    const res = await apiRequest<StudentApiRecord>('/api/students', {
        method: 'POST',
        body: data,
    });
    return res.data;
}

/**
 * Update an existing student.
 * Accepts FormData (multipart/form-data) so the profile photo can be uploaded.
 * PHP does not populate the request body for multipart PUT requests, so we
 * POST with Laravel method spoofing (`_method=PUT`).
 */
export async function updateStudent(id: string, data: FormData): Promise<StudentApiRecord> {
    data.append('_method', 'PUT');
    const res = await apiRequest<StudentApiRecord>(`/api/students/${id}`, {
        method: 'POST',
        body: data,
    });
    return res.data;
}

/**
 * Soft-delete a student.
 */
export async function deleteStudent(id: string): Promise<void> {
    await apiRequest(`/api/students/${id}`, { method: 'DELETE' });
}

/**
 * Fetch available classes (for filter dropdowns).
 * Uses per_page=100 to avoid truncation in dropdowns.
 */
export async function fetchClasses(): Promise<ClassOption[]> {
    const res = await apiRequest<ClassOption[]>('/api/classes?per_page=100');
    return res.data;
}

/**
 * Fetch sections for a given class (for cascading dropdown).
 * Uses the correct nested route: /api/classes/{classId}/sections
 */
export async function fetchSections(classId: string): Promise<SectionOption[]> {
    const res = await apiRequest<SectionOption[]>(`/api/classes/${classId}/sections`);
    return res.data;
}

/**
 * Fetch available streams (e.g. Science, Commerce) for dropdowns.
 */
export async function fetchStreams(): Promise<StreamOption[]> {
    const res = await apiRequest<StreamOption[]>('/api/streams?per_page=100');
    return res.data;
}

export type StateOption = { id: number; name: string };
export type DistrictOption = { id: number; name: string };

/**
 * Fetch Indian states from the address lookup API.
 */
export async function fetchStates(): Promise<StateOption[]> {
    const res = await apiRequest<StateOption[]>('/api/address-lookup/states');
    return res.data;
}

/**
 * Fetch districts for a given state ID.
 */
export async function fetchDistricts(stateId: number): Promise<DistrictOption[]> {
    const res = await apiRequest<DistrictOption[]>(`/api/address-lookup/states/${stateId}/districts`);
    return res.data;
}

export type CityOption = { id: number; name: string };

/**
 * Fetch cities for a given state ID.
 */
export async function fetchCities(stateId: number): Promise<CityOption[]> {
    const res = await apiRequest<CityOption[]>(`/api/address-lookup/states/${stateId}/cities`);
    return res.data;
}

/** Shape returned by GET /api/parents/search?phone= */
export interface ParentSearchResult {
    id: string;
    organization_id: string;
    first_name: string;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    gender: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    country: string | null;
    role: string;
    is_active: boolean;
    profile_photo_url: string | null;
    profile_photo_thumb_url: string | null;
    children: {
        id: string;
        enrollment_number: string;
        roll_number: string | null;
        first_name: string;
        last_name: string | null;
        relationship_type: string;
        is_primary: boolean;
    }[];
    created_at: string;
    updated_at: string;
}

/**
 * Search for an existing parent by phone number.
 * Uses GET /api/parents/search?phone=<phone>.
 * Returns the matching parent or null if not found.
 */
export async function searchParentByPhone(phone: string): Promise<ParentSearchResult | null> {
    try {
        const res = await apiRequest<ParentSearchResult>(`/api/parents/search?phone=${encodeURIComponent(phone)}`);
        return res.data;
    } catch {
        return null;
    }
}

// ── Helpers ─────────────────────────────────────────────────

/**
 * The transport route a student is allocated to, or null.
 *
 * Read off the detail record only — the list response doesn't carry it, so anything that
 * needs the route (the Collection Desk, to price the fare) has to ask for the student.
 * `transport_route_id` is the contract; the detail response still answers with `route_id`
 * on older backends, so both spellings are tolerated on the way in.
 */
export function studentTransportRouteId(s: StudentApiRecord): string | null {
    return s.route_id ?? s.transport_route_id ?? s.transport_route?.id ?? null;
}

/**
 * Map an API student record to a flat UI-friendly shape.
 */
export function mapStudentToUi(s: StudentApiRecord) {
    // List response: flat first_name/last_name at top level, no user object
    // Detail response: nested under user
    const firstName = s.first_name ?? s.user?.first_name ?? '';
    const lastName = s.last_name ?? s.user?.last_name ?? '';
    const primaryParent = s.parents?.[0] ?? null;
    const parentName = primaryParent
        ? (primaryParent.name ?? `${primaryParent.first_name}${primaryParent.last_name ? ' ' + primaryParent.last_name : ''}`.trim())
        : null;
    return {
        id: s.id,
        student_name: s.name ?? `${firstName}${lastName ? ' ' + lastName : ''}`.trim(),
        email: s.user?.email ?? null,
        enrollment_number: s.enrollment_number,
        roll_number: s.roll_number ?? null,
        class_id: s.current_class?.id ?? null,
        class_name: s.current_class?.name ?? null,
        section_id: s.current_section?.id ?? null,
        section_name: s.current_section?.name ?? null,
        status: s.enrollment_status,
        parent_name: parentName,
        parent_phone: primaryParent?.phone ?? null,
        parent_email: primaryParent?.email ?? null,
        father_name: s.father_name ?? null,
        mother_name: s.mother_name ?? null,
        father_contact: s.father_contact ?? null,
        aadhaar_number: s.aadhaar_number ?? null,
        // The list response sends `address` and `date_of_birth` flat; the detail response
        // nests them under `user`.
        address: s.address ?? s.user?.address ?? null,
        date_of_birth: s.date_of_birth ?? s.user?.date_of_birth ?? null,
        admission_date: s.admission_date ?? null,
        photo_url: s.user?.profile_photo_thumb_url ?? s.user?.profile_photo_url ?? null,
        documents: [] as { id: string; name: string; type: string; url: string; uploaded_at: string }[],
        metadata: (s.metadata || {}) as Record<string, unknown>,
    };
}
